import Check from '@/assets/icons/Check.svg';
import Cloud from '@/assets/icons/Cloud.svg';
import { NextUI } from '@/components/NextUI';
import { Checkbox } from '@heroui/checkbox';
import { Spinner } from '@heroui/spinner';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import React, { useState, useRef } from 'react';
import FormCard from '../../../../components/Layouts/Default/FormCard';
import { ContactCategoryEnum } from '@/features/contact/enums/contactCategory';
import { useTranslations } from 'next-intl';
import useAuth from '@/hooks/useAuth';
import useFirebaseUpload from '@/features/tool/hooks/useFirebaseUpload';
import { ContactService } from '@/features/contact/services/contactService';
import { supportStorage } from '@/lib/firebaseConfig';
import { v4 as uuidv4 } from 'uuid';

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const getFileKey = (file: File): string =>
  `${file.name}-${file.size}-${file.lastModified}`;

const MIME_TYPE_EXTENSIONS: Record<string, string> = {
  'application/pdf': 'pdf',
  'text/plain': 'txt',
  'text/csv': 'csv',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'pptx',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/x-msvideo': 'avi',
  'video/webm': 'webm',
};

const getSupportAttachmentFileName = (file: File): string => {
  const extensionFromName = file.name.split('.').pop()?.toLowerCase();
  const extension =
    MIME_TYPE_EXTENSIONS[file.type] ?? extensionFromName ?? 'bin';

  return `${uuidv4()}.${extension}`;
};

function Contact({
  data = {
    defaultOption: ContactCategoryEnum.QUESTION,
  },
  hideModal,
  onCallback,
}: {
  data?: {
    defaultOption: ContactCategoryEnum;
  };
  onCallback: (key?: string, data?: any) => void;
  hideModal: (data: any) => void;
}) {
  const t = useTranslations('contact_modal');
  const defaultOption = data?.defaultOption ?? ContactCategoryEnum.QUESTION;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { uploadFile } = useFirebaseUpload(supportStorage);
  const isSubmittingRef = useRef(false);

  const [formData, setFormData] = useState({
    email: '',
    message: '',
    askaquestion: defaultOption,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isChecked, setIsChecked] = useState(false);
  const [showPrivacyError, setShowPrivacyError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string>('');
  const [submitError, setSubmitError] = useState<string>('');
  const [hasAttachmentUploadFailure, setHasAttachmentUploadFailure] =
    useState(false);

  const MAX_ATTACHMENTS = 5;
  const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024 * 1024;
  const allowedFileTypes = [
    'application/pdf',
    'text/plain',
    'text/csv',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  ];
  const allowedFileExtensions = [
    'pdf',
    'txt',
    'csv',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'jpg',
    'jpeg',
    'png',
    'gif',
    'webp',
    'heic',
    'mp4',
    'mov',
    'avi',
    'webm',
  ];

  const CONTACT_CATEGORIES: Array<{
    label: string;
    value: ContactCategoryEnum;
  }> = [
    {
      label: t('categories.report'),
      value: ContactCategoryEnum.REPORT,
    },
    {
      label: t('categories.question'),
      value: ContactCategoryEnum.QUESTION,
    },
    {
      label: t('categories.suggest'),
      value: ContactCategoryEnum.SUGGEST,
    },
    {
      label: t('categories.contact'),
      value: ContactCategoryEnum.CONTACT,
    },
    {
      label: t('categories.feedback'),
      value: ContactCategoryEnum.FEEDBACK,
    },
  ];

  const getFileValidationError = (file: File): string | null => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    const isImageOrVideo = /^(image|video)\//.test(file.type);

    if (file.size > MAX_ATTACHMENT_SIZE) {
      return t('validation.file_size');
    }

    if (
      !isImageOrVideo &&
      !allowedFileTypes.includes(file.type) &&
      (!extension || !allowedFileExtensions.includes(extension))
    ) {
      return t('validation.file_type');
    }

    return null;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);

    if (files.length === 0) return;

    const selectedFileKeys = new Set(attachedFiles.map(getFileKey));
    const acceptedFiles: File[] = [];
    const rejectionReasons = new Set<string>();
    const availableSlots = MAX_ATTACHMENTS - attachedFiles.length;
    let rejectedFileCount = 0;

    files.forEach((file) => {
      const validationError = getFileValidationError(file);

      if (validationError) {
        rejectedFileCount += 1;
        rejectionReasons.add(validationError);
        return;
      }

      const fileKey = getFileKey(file);
      if (selectedFileKeys.has(fileKey)) {
        rejectedFileCount += 1;
        rejectionReasons.add(t('validation.file_duplicate'));
        return;
      }

      if (acceptedFiles.length >= availableSlots) {
        rejectedFileCount += 1;
        rejectionReasons.add(t('validation.file_count'));
        return;
      }

      selectedFileKeys.add(fileKey);
      acceptedFiles.push(file);
    });

    if (acceptedFiles.length > 0) {
      setAttachedFiles((currentFiles) => [...currentFiles, ...acceptedFiles]);
    }
    setFileError(
      rejectionReasons.size > 0
        ? t(
            rejectedFileCount === 1
              ? 'validation.file_rejected'
              : 'validation.files_rejected',
            {
              count: rejectedFileCount,
              reasons: Array.from(rejectionReasons).join(' '),
            }
          )
        : ''
    );
    event.target.value = '';
  };

  const handleFileRemove = (index: number) => {
    setAttachedFiles((files) =>
      files.filter((_, fileIndex) => fileIndex !== index)
    );
    setFileError('');
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.email) {
      newErrors.email = t('validation.email_required');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('validation.email_invalid');
    }
    if (!formData.message.trim()) {
      newErrors.message = t('validation.message_required');
    }
    if (!formData.askaquestion)
      newErrors.askaquestion = t('validation.category_required');

    const hasPrivacyError = !isChecked;
    setShowPrivacyError(hasPrivacyError);
    setErrors(newErrors);
    return !hasPrivacyError && Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isSubmittingRef.current || !validateForm()) return;

    isSubmittingRef.current = true;
    setIsLoading(true);
    setSubmitError('');
    setHasAttachmentUploadFailure(false);

    try {
      const uploadResults = await Promise.allSettled(
        attachedFiles.map((file) =>
          uploadFile(file, 'support/', {
            includeDownloadURL: false,
            fileName: getSupportAttachmentFileName(file),
          })
        )
      );
      const attachments = uploadResults.flatMap((result) =>
        result.status === 'fulfilled' ? [result.value.key] : []
      );
      const attachmentUploadFailed =
        attachments.length !== attachedFiles.length;

      if (attachmentUploadFailed) {
        setHasAttachmentUploadFailure(true);
      }

      const userAgent = navigator.userAgent || 'Unknown';
      const device = /Mobi|Android|iPhone|iPad|iPod/i.test(userAgent)
        ? 'Mobile'
        : navigator.platform || 'Desktop';

      await ContactService.submitSupportRequest({
        submissionReason: formData.askaquestion,
        message: formData.message,
        email: formData.email,
        userId: user?.uid || 'anonymous',
        browser: userAgent,
        device,
        plan: user?.planUid || 'Free',
        ...(attachments.length > 0 ? { attachments } : {}),
      });

      setIsSuccess(true);
      setFormData({
        email: '',
        message: '',
        askaquestion: defaultOption,
      });
      setAttachedFiles([]);
    } catch (error) {
      console.error('Failed to submit form:', error);
      setSubmitError(
        error instanceof Error ? error.message : t('error.message')
      );
    } finally {
      setIsLoading(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <NextUI.Modal
      className="max-w-[500px]"
      classNames={{ content: 'py-8 !px-4 md:!px-8 lg:px-12' }}
      defaultOpen={true}
      name={'Contact'}
    >
      {isLoading ? (
        <div className="flex flex-col items-center justify-center min-h-[300px]">
          <Spinner size="lg" color="primary" />
          <p className="mt-4 text-gray-dark">{t('loading')}</p>
        </div>
      ) : isSuccess ? (
        <div className="flex flex-col items-center justify-center min-h-[300px] text-center">
          <div className="relative mb-6">
            <Image
              src={Check}
              alt="Success"
              width={80}
              height={80}
              className="animate-scale-in"
            />
            <Image
              src={Cloud}
              alt="Message sent"
              width={40}
              height={40}
              className="absolute -bottom-2 -right-2 animate-slide-up"
            />
          </div>
          <h2 className="h5 font-semibold text-dark mb-4 animate-fade-in">
            {t('success.title')}
          </h2>
          <p className="text-gray-dark animate-fade-in delay-100">
            {hasAttachmentUploadFailure
              ? t('success.message_without_attachments')
              : t('success.message')}
          </p>
        </div>
      ) : (
        <>
          <h2 className="h5 font-semibold text-center text-dark">
            {t('title')}
          </h2>

          <p className="mt-6 body-normal text-center text-gray-dark">
            {t('subtitle')}
          </p>

          {submitError && (
            <div className="mt-4 rounded-lg bg-danger-50 p-3 text-sm text-danger-600">
              {submitError}
            </div>
          )}

          <FormCard
            actionText="Submit"
            className="mt-8"
            noValidate
            classNames={{
              actionButton: 'max-w-[160px]',
              actionWrapper: 'mt-8 pb-6',
              formBody: 'p-0 gap-5',
            }}
            onSubmit={handleSubmit}
            actionProps={{
              isDisabled: isLoading,
            }}
          >
            <div>
              <NextUI.Select
                value={formData.askaquestion}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    askaquestion: e.target.value as ContactCategoryEnum,
                  }))
                }
                data={CONTACT_CATEGORIES}
                variant="outline"
                radius="sm"
                isRequired
                errorMessage={errors.askaquestion}
                isInvalid={!!errors.askaquestion}
                isDisabled={isLoading}
                selectedKeys={[formData.askaquestion]}
              />
            </div>

            <div>
              <NextUI.TextArea
                value={formData.message}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    message: e.target.value,
                  }));
                  if (errors.message) {
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.message;
                      return newErrors;
                    });
                  }
                }}
                placeholder={t('form.message_placeholder')}
                variant="outline"
                radius="sm"
                classNames={{
                  input: 'min-h-[102px]',
                  inputWrapper: clsx(
                    errors.message &&
                      '!border-danger-500 focus-within:!border-danger-500 focus-within:hover:!border-danger-500'
                  ),
                }}
                errorMessage={errors.message}
                isInvalid={!!errors.message}
                isDisabled={isLoading}
              />
            </div>

            <div>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="hidden"
                accept=".jpg,.jpeg,.png,.gif,.webp,.heic,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp4,.mov,.avi,.webm"
                multiple
              />

              <div
                onClick={handleAttachClick}
                className={clsx(
                  'flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors',
                  fileError
                    ? 'border-danger-300 text-danger-500'
                    : attachedFiles.length > 0
                      ? 'border-primary-blue text-primary-blue bg-primary-50'
                      : 'border-gray-300 text-gray-500 hover:border-gray-400'
                )}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="flex-shrink-0"
                >
                  <path
                    d="M21.44 11.05L12.25 20.24C11.1242 21.3658 9.59722 21.9983 8.005 21.9983C6.41278 21.9983 4.88583 21.3658 3.76 20.24C2.63417 19.1142 2.00167 17.5872 2.00167 15.995C2.00167 14.4028 2.63417 12.8758 3.76 11.75L12.95 2.56C13.7006 1.80943 14.7186 1.38825 15.78 1.38825C16.8414 1.38825 17.8594 1.80943 18.61 2.56C19.3606 3.31057 19.7818 4.32857 19.7818 5.39C19.7818 6.45143 19.3606 7.46943 18.61 8.22L9.41 17.41C9.03479 17.7852 8.52556 17.9948 7.995 17.9948C7.46444 17.9948 6.95521 17.7852 6.58 17.41C6.20479 17.0348 5.99524 16.5256 5.99524 15.995C5.99524 15.4644 6.20479 14.9552 6.58 14.58L15.07 6.1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                <span className="text-sm flex-1">
                  {attachedFiles.length > 0
                    ? t('form.files_selected', { count: attachedFiles.length })
                    : t('form.file_label')}
                </span>
              </div>

              {fileError && (
                <div className="mt-1 text-danger-500 text-xs">{fileError}</div>
              )}

              {attachedFiles.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-gray-400">
                  {attachedFiles.map((file, index) => (
                    <li key={getFileKey(file)} className="flex gap-2">
                      <span className="min-w-0 flex-1 truncate">
                        {file.name} ({formatFileSize(file.size)})
                      </span>
                      <button
                        type="button"
                        onClick={() => handleFileRemove(index)}
                        className="-mr-1 flex h-6 w-6 items-center justify-center text-lg leading-none text-gray-400 hover:text-gray-600"
                        aria-label={t('form.remove_file', { name: file.name })}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <NextUI.Input
                value={formData.email}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, email: e.target.value }));
                  if (errors.email) {
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.email;
                      return newErrors;
                    });
                  }
                }}
                type="email"
                variant="outline"
                radius="sm"
                placeholder={t('form.email_placeholder')}
                errorMessage={errors.email}
                isInvalid={!!errors.email}
                isDisabled={isLoading}
              />
            </div>

            <div>
              <div className="flex gap-2">
                <Checkbox
                  isSelected={isChecked}
                  onValueChange={(value: boolean) => {
                    setIsChecked(value);
                    if (value) setShowPrivacyError(false);
                  }}
                  classNames={{
                    wrapper: clsx(
                      'mt-0.5',
                      showPrivacyError && 'border-danger-500'
                    ),
                  }}
                />
                <label
                  className="text-sm cursor-pointer flex-1 text-gray-dark"
                  onClick={() => setIsChecked(!isChecked)}
                >
                  {t('form.terms_part1')}{' '}
                  <Link
                    href="/terms"
                    target="_blank"
                    className="text-primary-blue"
                  >
                    {t('form.terms_link')}
                  </Link>{' '}
                  {t('form.terms_part2')}{' '}
                  <Link
                    href="/privacy"
                    target="_blank"
                    className="text-primary-blue"
                  >
                    {t('form.privacy_link')}
                  </Link>
                </label>
              </div>

              {showPrivacyError && (
                <div className="mt-1 text-danger-500 text-sm">
                  {t('form.terms_error')}
                </div>
              )}
            </div>
          </FormCard>
        </>
      )}
    </NextUI.Modal>
  );
}

export default Contact;
